import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { useSceneDispatch, useSceneSelector } from '@visionair/scene-state-3js';

import {
  showNotificationError,
  showNotificationSuccess,
} from '../../../modules/notifications/showNotification';
import saveCenterline from '../../../modules/scene/actions/airwayView/saveCenterline';
import undoCenterline from '../../../modules/scene/actions/airwayView/undoCenterline';
import redoCenterline from '../../../modules/scene/actions/airwayView/redoCenterline';
import {
  getCenterlineNeedsUpdate,
  getRedoEnabled,
  getUndoEnabled,
} from '../../../modules/scene/selectors';

import viewerIcon from '../../../images/3d-viewer.svg';
import undoIconActive from '../../../images/centerline_tools/undo_active.svg';
import undoIcon from '../../../images/centerline_tools/undo.svg';
import redoIconActive from '../../../images/centerline_tools/redo_active.svg';
import redoIcon from '../../../images/centerline_tools/redo.svg';
import { SEG_STATUS } from '../../../settings/segmentations';

export default function AiwayViewerHeader({ seg }) {
  const { centerlineNeedsUpdate, disableUndo, disableRedo } = useSceneSelector(state => ({
    centerlineNeedsUpdate: getCenterlineNeedsUpdate(state),
    disableUndo: !getUndoEnabled(state),
    disableRedo: !getRedoEnabled(state),
  }));

  const sceneDispatch = useSceneDispatch();
  const reduxDispatch = useDispatch();

  const isUndoDisabled = disableUndo || seg.status !== SEG_STATUS.SUCCESS;
  const isRedoDisabled = disableRedo || seg.status !== SEG_STATUS.SUCCESS;
  const handleSave = useCallback(() => {
    sceneDispatch(saveCenterline(seg.id))
      .then(() => {
        reduxDispatch(showNotificationSuccess('Centerline saved successfully'));
      })
      .catch(err => {
        console.error(err); // eslint-disable-line no-console
        reduxDispatch(
          showNotificationError('There was a problem saving centerline. Please try again.'),
        );
      });
  }, [reduxDispatch, sceneDispatch, seg]);

  useEffect(() => {
    if (!centerlineNeedsUpdate) {
      return;
    }

    const listener = evt => {
      // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
      evt.preventDefault();
      // eslint-disable-next-line no-param-reassign
      evt.returnValue = '';
    };

    window.addEventListener('beforeunload', listener);

    handleSave();

    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('beforeunload', listener);
  }, [centerlineNeedsUpdate, handleSave]);

  const handleUndo = useCallback(() => {
    if (!isUndoDisabled) {
      sceneDispatch(undoCenterline());
    }
  }, [sceneDispatch, isUndoDisabled]);

  const handleRedo = useCallback(() => {
    if (!isRedoDisabled) {
      sceneDispatch(redoCenterline());
    }
  }, [sceneDispatch, isRedoDisabled]);

  const handleKeyPress = useCallback(
    event => {
      switch (event.key) {
        case 'u':
        case 'z':
          handleUndo();
          break;
        case 'r':
          handleRedo();
          break;
        default:
      }
    },
    [handleUndo, handleRedo],
  );

  useEffect(() => {
    // attach the event listener
    document.addEventListener('keydown', handleKeyPress);

    // remove the event listener
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <Container>
      <div>
        <img src={viewerIcon} alt='' />
        3D Viewer (Centerline Edit)
      </div>
      <div style={{ display: 'flex' }}>
        <IterationBtn disabled={isUndoDisabled} onClick={handleUndo}>
          <img src={isUndoDisabled ? undoIcon : undoIconActive} alt='undo' />
          Undo
        </IterationBtn>
        <IterationBtn disabled={isRedoDisabled} onClick={handleRedo}>
          <img src={isRedoDisabled ? redoIcon : redoIconActive} alt='redo' />
          Redo
        </IterationBtn>
      </div>
    </Container>
  );
}

const Container = styled.div`
  height: 48px;
  background-image: linear-gradient(to bottom, #414141, #0d0d0d);
  padding: 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  font-size: 12px;
  font-weight: bold;

  img {
    margin-right: 10px;
  }
`;

const btnMixin = css`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;

  border: none;
  background: none;
  outline: none !important;

  &:disabled {
    opacity: 0.4;
    color: white;
    cursor: not-allowed;
  }
`;

const IterationBtn = styled.button`
  ${btnMixin}

  color: #ffd800;
  margin-right: 20px;
`;
